
import { cartGet, cartAdd, cartGetCurrent, cartUpdate, cartChange } from '@process-creative/slate-theme-tools';

import { bindAssignedElements,removeBundleFromCart } from './utils';
import { BundleTitle } from './bundle-title';
import { BundleImage } from './bundle-image';
import { BundleNavigation } from './bundle-navigation';
import { BundleProductSelector } from './bundle-product-selector';
import { BundleSummary } from './bundle-summary';
import { BundleGlass } from './bundle-glass';

export const CLASS_OPEN = 'is-bundle-builder-open';

const template = document.createElement('template');
template.innerHTML = `
  <style>
    :host {
      height: 100%;
      width: 100%;
    }

    :root {
      
    }

    :host(.close) {
      stroke: #ffffff;
    }

    :host(.o-bundle-builder) {
      background-image: var(--background-image);
      background-position: bottom;
      background-repeat: no-repeat;
      background-size: cover;
      font-family: 'BASEL CLASSIC', sans-serif;
    }

    .o-bundle-builder__content {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
    }

    ::slotted(.o-bundle-builder__column) {
      width: 100%;
      height: 100%;
    }

    ::slotted(.o-bundle-builder__column:first-of-type) {
      color: #FFFFFF;
      display: flex;
      align-items: center;
      flex-direction: column;
      justify-content: center;
    }

    ::slotted(.o-bundle-builder__column:last-of-type) {
      background-color: #FFFFFF;
      border-top-left-radius: 0.875rem;
      border-bottom-left-radius: 0.875rem
      padding: 2.5rem;
      text-align: center;
      max-width: 32.5rem;
      width: 100%;
    } 

    @media screen and (max-width: 991px) {
      :host(.o-bundle-builder) {
        background-image: none;
        overflow-y: scroll;
      } 

      .o-bundle-builder__content {
        flex-direction: column;
        height: auto;
      }

      ::slotted(.o-bundle-builder__column:first-of-type) {
        background-image: var(--mobile-background-image);
        background-position: bottom;
        background-repeat: no-repeat;
        background-size: cover;
        max-width: 100%;
        width: 100%;
      }

      ::slotted(.o-bundle-builder__column:last-of-type) {
        border-radius: 0;
        padding: 2rem 1.25rem;
        max-width: 100%;
        width: 100%;
        overflow-y: scroll;
      }
    }

  </style>

  <div class="o-bundle-builder__content" part="root">
    <slot></slot>
  </div>
`;

type LineItemProps = {
  _bundle_id: string;
  'Bundle Gift Message'?: string;
  _builder_settings?: string;
}

type BundleProduct = {
  id:number;
  image:string;
  product_title:string;
  price?:number;
}

type BuilderSettings = {
  products: string[];
  hasGlass: boolean;
  note: string;
  bundleId:string;
}

type BundleStep = BundleProduct | null;

type BundleState = [ BundleStep, BundleStep, BundleStep ];

export class BundleBuilder extends HTMLElement {

  // Properties
  private readonly root:HTMLDivElement|null;
  private bundleTitle: BundleTitle;
  private image: BundleImage;
  private navigation: BundleNavigation;
  private productSelectors: BundleProductSelector[];
  private summary: BundleSummary;
  private bundleGlassElement: BundleGlass;
  private steps:(BundleProductSelector|BundleSummary)[];

  private readonly boxJSON = JSON.parse(this.getAttribute('box-product') || '{}');
  private readonly glassJSON = JSON.parse(this.getAttribute('glass-product') || '{}');
  private readonly hideGlassProduct:boolean = this.getAttribute('hide-glass-product') === 'true';
  private readonly glassIsMandatory:boolean = this.getAttribute('glass-is-mandatory') === 'true';
  private bundleId:string = this.getAttribute('bundle-id') ?? '';
  private newBundleId:string = '';
  private editMode:boolean = false;

  private bundleProducts:BundleState = [ null, null, null ];
  private bundleBox:BundleProduct;
  private bundleGlass:BundleProduct;
  private note:string = '';

  private currentStep:number = 0;
  private previousStep:number = 0;

  constructor() {
    super();

    const shadowRoot = this.attachShadow({ mode: 'open' });
    shadowRoot.appendChild(template.content.cloneNode(true));

    this.root = shadowRoot.querySelector('.bundle-builder')!;

    const slotElement = shadowRoot.querySelector('slot')
    const getSubComponent = bindAssignedElements(slotElement!);

    this.bundleTitle = getSubComponent<BundleTitle>('bundle-title')!;
    this.image = getSubComponent<BundleImage>('bundle-image')!;
    this.navigation = getSubComponent<BundleNavigation>('bundle-navigation')!;
    this.productSelectors = getSubComponent<BundleProductSelector[]>('bundle-product-selector')!;
    this.summary = getSubComponent<BundleSummary>('bundle-summary')!;
    this.bundleGlassElement = getSubComponent<BundleGlass>('bundle-glass')!;

    this.steps = [...this.productSelectors, this.summary];

    this.addEventListener('navigation', this.handleNavigationEvent);
    this.addEventListener('product_select', this.handleProductSelectEvent);
    this.addEventListener('product_remove', this.handleProductRemoveEvent);
    this.addEventListener('add_to_cart', this.addBundleToCart);
    
    if (!this.hideGlassProduct) {
      this.addEventListener('glass_select', this.handleGlassSelectEvent);
    }

    // Auto-add glass if mandatory
    if (this.glassIsMandatory && !this.hideGlassProduct) {
      this.setGlassProduct(true);
    }
    
    // set base state on summary
    this.setSummaryUI();

    // set base navigation state
    this.navigate();
  }

  get totalCost():number {
    const totalCentValue = [ 
      this.bundleBox, 
      ...this.bundleProducts, 
      this.bundleGlass 
    ].filter(item => item)
    .reduce((acc, item) => acc += (item?.price ?? 0), 0);

    return parseFloat((totalCentValue / 100).toFixed(2));
  }

  get glassIsSelected():boolean {
    return !!this.bundleGlass;
  }

  get summaryMessage():string {
    return this.summary.noteElement.value;
  }

  get selectedBox() {
    return this.boxJSON.variants.find(boxVariant => boxVariant.pair_with_glass === this.glassIsSelected)
  }

  get allBundleChildProducts() {
    return [
      this.selectedBox,
      ...this.bundleProducts,
      this.bundleGlass
    ].filter(item => item);
  }

  get bundleParentProperties() {

    const bundleChildrenTitles = this.allBundleChildProducts.reduce((acc, product) => {
      if (!product.product_title) return acc;
      if (acc[product.product_title]) acc[product.product_title]++;
      else acc[product.product_title] = 1;
      return acc;
    }, {});

    return {
      'Bundle Gift Message': this.summaryMessage,
      _bundle_children: Object.keys(bundleChildrenTitles).map(title => {
        const qty = bundleChildrenTitles[title];
        return `${title} x ${qty}`;
      }).join('|'),
      _builder_settings: JSON.stringify({
        products: this.bundleProducts.map(product => product?.id), // No need to filter any null values from this array. All products must be set before this function can run
        hasGlass: this.glassIsSelected,
        note: this.summaryMessage,
        bundleId: this.newBundleId
      }),
      _bundle_parent_id: this.newBundleId,
    }
  }

  private navigationBlocked(stepToNavTo) {
    return stepToNavTo === this.steps.length - 1 && // trying to go to final step
      this.bundleProducts.some(item => !item) // and some products are missing from bundle
  }

  /** 
   * Sets the stepIndex of the next step to navigate to
   * This function call should be followed by this.navigate();
   */
  private setNextStep(stepIndex:number) {

    if (this.navigationBlocked(stepIndex)) return console.warn('Please select another product!');

    const prevStep = this.currentStep;
    this.previousStep = prevStep;
    this.currentStep = stepIndex;

  }

  private handleProductSelectEvent(event) {

    const currentProductList = this.productSelectors[this.currentStep];

    // Turn off current UI
    currentProductList.setInactiveTiles();

    // Set Internal State
    const selectedVariantTile = event.detail.ctx;
    // Add product to correct index in this.bundleProducts
    this.bundleProducts[this.currentStep] = selectedVariantTile.variant;


    /**SET UI */
    this.image.setBottleImage(this.currentStep, selectedVariantTile.variant.image);
    this.bundleTitle.updateTitle(this.totalCost);
    this.setSummaryUI();
    // Update product tile state
    selectedVariantTile.setActive(true);

    /** UPDATE CURRENT STEP AND RENDER NEXT STEP */
    const stepToNavTo = this.previousStepWasSummaryPage ? this.previousStep : this.currentStep + 1;
    this.setNextStep(stepToNavTo);
    this.navigate();
  }

  private handleGlassSelectEvent(event) {
    // Prevent removing glass if mandatory
    if (this.glassIsMandatory && this.glassIsSelected) return;
    
    this.bundleGlass = !this.glassIsSelected ? this.glassJSON : null;
    this.setGlassUI();

    const customEvent = new CustomEvent('variant_change', {
      detail: { addGlass: this.glassIsSelected },
      bubbles: true,
      composed: true
    })

    this.dispatchEvent(customEvent);
  }

  public setGlassProduct(setGlass:boolean) {
    this.bundleGlass = setGlass ? this.glassJSON : null;
    this.setGlassUI();
  }

  private setGlassUI() {
    this.setSummaryUI();

    this.image.setHasGlass(this.glassIsSelected)
    this.bundleTitle.updateTitle(this.totalCost);

    this.bundleGlassElement?.setGlassIsAdded(this.glassIsSelected);
  }

  private get previousStepWasSummaryPage():boolean {
    return this.previousStep === this.steps.length - 1;
  }

  private handleProductRemoveEvent(event) {
    const selectedVariantTile = event.detail.ctx;
    const currentProductList = this.productSelectors[this.currentStep];

    currentProductList.setInactiveTiles();

    this.bundleProducts[this.currentStep] = null;

    this.image.setBottleImage(this.currentStep, null);
    this.image.setStep(this.currentStep);

    this.bundleTitle.updateTitle(this.totalCost);
    this.summary.setAddToCartText(this.totalCost);
  }

  private showStep() {
    this.steps.forEach((step, index) => {
      step.setActive?.(index === this.currentStep);
    });
  }

  private navigate() {
    this.showStep();
    this.image.setStep?.(this.currentStep);
    this.navigation.setActive?.(this.currentStep);
  }

  private setSummaryUI() {
    this.summary.setProducts?.(this.bundleProducts);
    this.summary.updateGlass?.(this.glassIsSelected);
    this.summary.setAddToCartText?.(this.totalCost);
  }

  private handleNavigationEvent(event) {
    const { stepIndex } = event.detail;

    this.setNextStep(stepIndex);
    this.navigate();

  }

  public async addBundleToCart() {
    // generate bundle ID
    this.newBundleId = `${Math.floor(Math.random() * Date.now())}`;
  
    // get all unique variant IDS and their QTYs
    const productVariantsWithAmt = this.allBundleChildProducts.reduce((acc, product) => {
      if (acc[product.id]) acc[product.id]++;
      else acc[product.id] = 1;
      return acc;
    }, {});

    /** IMPORTANT */
    /**
     * When the cart transform API is fixed, we need to refactor this code slightly
     * Anything related to the bundle parent can be moved outside of the following map and down towards the next comment
     * The products added to the cart, just require the _bundle_id line item property
     * If you need more information, talk to Josh
     */
    const addToCartItems = Object.keys(productVariantsWithAmt).map(product_id => {
      const productId = parseInt(product_id);
      const isBundleParentProduct = product_id == this.selectedBox.id // check if the product ID equals one of the ids for the box product
      const qty = productVariantsWithAmt[product_id];

      let properties:LineItemProps = {
        _bundle_id: this.newBundleId
      }

      if (isBundleParentProduct) {

        properties = {
          ...properties,
          ...this.bundleParentProperties
        }
      }

      return {
        id: productId,
        quantity: qty as number,
        properties
      }
    });

    if (this.editMode && this.bundleId.length) {
      await removeBundleFromCart(this.bundleId);
    }

    await cartAdd({ items: addToCartItems });

    /**IMPORTANT */
    /**
     * When the Cart Transform API is fixed, the following code can be uncommented
     * Depending on how the API is fixed may require some changes to the below code 
     * If you need more information, talk to Josh
    */
    /**
      const cartCurrent = await cartGet();

      let addedBundleLineIndex:number|null = null;
      const addedBundle = cartCurrent.items.find((line_item, line_index) => {
        const lineItemContainsBundle = line_item.variant_id === 43212261359777 && !line_item.properties._bundle_parent_id;
        if (lineItemContainsBundle) addedBundleLineIndex = line_index;
        return lineItemContainsBundle;
      });

      await cartChange({
        id: addedBundle!.key,
        // line: addedBundleLineIndex,
        properties: this.bundleParentProperties,
        // quantity: 1 // Changing this to another number has weird side effects
        // The increased price reflects the amount of qty, however the qty selector in the cart only shows a single item
      });
    */

    this.editMode = false;
    this.emitSlideTrigger(false);

    this.clearBundle();
  }

  private clearBundle() {
    this.bundleId = '';
    this.newBundleId = '';
    this.bundleProducts = [ null, null, null ];
    
    this.setSummaryUI();
    this.summary.setNote('');
    this.image.resetImage();

    this.productSelectors.forEach((selector, index) => {
      selector.setInactiveTiles();
    });

    this.bundleTitle.updateTitle(this.totalCost);

    this.setNextStep(0);
    this.previousStep = 0;
    this.navigate();
  }

  emitSlideTrigger(slideShouldOpen:boolean) {
    const customEvent = new CustomEvent('trigger_open', {
      detail: slideShouldOpen, 
      bubbles: true,
      composed: true
    })

    this.dispatchEvent(customEvent);
  }

  public editBundle(builderSettings:BuilderSettings) {

    const { products, hasGlass, note } = builderSettings;

    this.setGlassProduct(hasGlass);

    this.productSelectors.forEach((selector, index) => {
      selector.loadProduct(products[index]);
    });

    this.summary.setNote(note);

    this.editMode = true;

    this.emitSlideTrigger(true);

  }
}

if(!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}